import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { URLS } from '@shared/utils/urls';
import { getEventTypeConfig } from '@config';
import type { EventType } from '@graphql/generated-base';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { generateDescriptionKeywords } from '@shared/utils/seo';
import { Seo } from '@shared/components/Seo';
import { useTranslation } from '@shared/core';
import { useExtractEventPhoto } from '@shared/utils/eventPhoto';
import { shouldRenderGraphicAccent } from '@shared/utils/graphicAccent';
import { useMasthead } from '@apps/guest/packages/layout-engine/layouts/hooks/useMasthead/useMasthead';
import { StyleApplicatorScript } from '@shared/components/StyleApplicatorScript';

/**
 * Adds a query param to the image url to optimize the image from Media Service and add a source query param
 */
const addOptimizationQueryParam = (url: Maybe<string>): string | undefined => {
  if (!url) return undefined;
  const hasQueryParam = url.includes('?');
  const separator = hasQueryParam ? '&' : '?';
  return `${url}${separator}rendition=small&source=websitepreview`;
};

interface Props
  extends Readonly<{
    handle: Maybe<string>;
    eventDisplayName: Maybe<string>;
    imageUrl: Maybe<string>;
    hideFromSearchEngines: boolean;
    ownerFirstName: Maybe<string>;
    ownerLastName: Maybe<string>;
    fianceeFirstName: Maybe<string>;
    pageData: ReadonlyArray<{ id: string; disabled: boolean; type: string; pageSlug: string }>;
    eventType: EventType;
    accentPreloadUrl: string | null;
    mastheadPreloadUrl: string | null;
    mastheadMobilePreloadUrl: string | null;
  }> {}

export const Root = (props: Props) => {
  const {
    handle,
    eventDisplayName,
    imageUrl,
    hideFromSearchEngines,
    ownerFirstName,
    ownerLastName,
    fianceeFirstName,
    pageData,
    eventType,
    accentPreloadUrl,
    mastheadPreloadUrl,
    mastheadMobilePreloadUrl,
  } = props;
  const { t } = useTranslation('websitePages');

  const keywords = useMemo(() => generateDescriptionKeywords(t, pageData), [t, pageData]);
  const userInfo = useMemo(
    () => ({
      partnerOneFirstName: ownerFirstName || '',
      partnerOneLastName: ownerLastName || '',
      partnerTwoFirstName: fianceeFirstName || '',
    }),
    [ownerFirstName, ownerLastName, fianceeFirstName],
  );

  const pageTitle = useMemo(() => getEventTypeConfig(eventType).guestSitePageTitle(eventDisplayName || '', userInfo, t) || '', [eventType, eventDisplayName, userInfo, t]);

  const pageDescription = useMemo(
    () => getEventTypeConfig(eventType).guestSitePageDescription(eventDisplayName || '', userInfo, keywords, t),
    [eventType, eventDisplayName, userInfo, keywords, t],
  );
  const pageUrl = `https://withjoy.com/${handle}/`;

  return (
    <>
      <Seo
        title={pageTitle}
        description={pageDescription}
        canonicalURL={pageUrl}
        name="Joy"
        url={pageUrl}
        ogImage={addOptimizationQueryParam(imageUrl)}
        author="@JoytheApp"
        twitterSite="@JoytheApp"
      />
      <Helmet>
        <link
          href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital@0;1&display=swap"
          rel="stylesheet"
        />
        {hideFromSearchEngines && (
          <meta
            name="robots"
            content="noindex"
          />
        )}
        {accentPreloadUrl && (
          <link
            rel="preload"
            as="image"
            href={accentPreloadUrl}
          />
        )}
        {/* The masthead is painted as a CSS background, which the browser's preload scanner can't discover.
            Media-scoped per sm2 (768px) to mirror the responsive backgroundImage in masthead.styles.ts. */}
        {mastheadPreloadUrl && (
          <link
            rel="preload"
            as="image"
            href={mastheadPreloadUrl}
            media="(min-width: 768px)"
          />
        )}
        {mastheadMobilePreloadUrl && (
          <link
            rel="preload"
            as="image"
            href={mastheadMobilePreloadUrl}
            media="(max-width: 767px)"
          />
        )}
      </Helmet>
      <StyleApplicatorScript />
    </>
  );
};
/**
 * This supplies \<Helmet /> from react-helmet-async
 * Provides SEO meta tags and title information
 * Note: Font preloading is now handled by FontPackProvider
 */
export const GuestSiteHead: React.FC<{ event: GuestSiteEvent | undefined | null }> = ({ event }) => {
  // Primitive deps keep the headData memo (and Helmet reconciliation) stable.
  const resolvedMasthead = useMasthead(event?.eventDesign);
  const mastheadPreloadUrl = resolvedMasthead?.assetUrl ?? null;
  const mastheadMobilePreloadUrl = resolvedMasthead?.mobileAssetUrl ?? null;
  const eventPhoto = useExtractEventPhoto(event);
  const headData = useMemo(() => {
    const accentId = event?.eventDesign?.accent;

    return event
      ? {
          hideFromSearchEngines: event.settings.hideFromSearchEngines,
          eventDisplayName: event.info.eventDisplayName,
          imageUrl: eventPhoto?.url,
          handle: event.website,
          ownerFirstName: event.info.ownerFirstName,
          ownerLastName: event.info.ownerLastName,
          fianceeFirstName: event.info.fianceeFirstName,
          pageData: event.pages,
          eventType: event.info.eventType,
          accentPreloadUrl: shouldRenderGraphicAccent(accentId) ? URLS.graphicAccent(accentId!, 2) : null,
          mastheadPreloadUrl,
          mastheadMobilePreloadUrl,
        }
      : undefined;
  }, [event, mastheadPreloadUrl, mastheadMobilePreloadUrl, eventPhoto]);

  return headData ? <Root {...headData} /> : null;
};
